/**
 * SimpleInteractiveRenderer - Minimal HTML content renderer
 *
 * Renders HTML content in an iframe. For viewport-height sims (100vh),
 * uses a fixed 720px container. For content-sized sims, measures and
 * resizes after load.
 */

import React, { useRef, useEffect, useState } from 'react';

interface SimpleInteractiveRendererProps {
  /** Raw HTML content/blob to render directly */
  htmlBlob?: string;
  /** URL source for iframe content */
  htmlSrc?: string | null;
  /** Additional CSS classes */
  className?: string;
  /** Custom styling */
  style?: React.CSSProperties;
  /** Minimum height for the iframe */
  minHeight?: number;
}

const VIEWPORT_HEIGHT = 720;

const SimpleInteractiveRenderer: React.FC<SimpleInteractiveRendererProps> = ({
  htmlBlob,
  htmlSrc,
  className = '',
  style = {},
  minHeight = 400,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const usesViewport = htmlBlob
    ? /100vh/.test(htmlBlob) || /overflow:\s*hidden/.test(htmlBlob)
    : false;
  const [contentHeight, setContentHeight] = useState<number | null>(
    usesViewport ? VIEWPORT_HEIGHT : null
  );

  // Create complete HTML document from blob content
  const createFullHTML = (content: string): string => {
    if (!content) {
      return '';
    }

    if (content.includes('<html') || content.includes('<!DOCTYPE')) {
      return content;
    }

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Interactive Content</title>
  <style>
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      height: auto;
    }
    body {
      padding: 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background-color: #ffffff;
      color: #333333;
      line-height: 1.5;
    }
    .content-container {
      width: 100%;
    }
    img, video, iframe {
      max-width: 100%;
      height: auto;
    }
  </style>
</head>
<body>
  <div class="content-container">
    ${content}
  </div>
</body>
</html>
    `;
  };

  useEffect(() => {
    setContentHeight(usesViewport ? VIEWPORT_HEIGHT : null);
  }, [usesViewport]);

  useEffect(() => {
    if (usesViewport) return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    let resizeObserver: ResizeObserver | null = null;

    const measureHeight = (doc: Document): number => {
      const body = doc.body;
      const html = doc.documentElement;
      return Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.clientHeight || 0,
        html?.scrollHeight || 0,
        html?.offsetHeight || 0,
        minHeight
      );
    };

    const handleLoad = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;

        // Check if the loaded content is a fixed-viewport app
        // (uses 100vh or overflow:hidden on body -- signals "fill my container")
        let isFixedViewport = false;
        doc.querySelectorAll('style').forEach((s) => {
          if (
            s.textContent &&
            (/100vh/.test(s.textContent) ||
              /overflow:\s*hidden/.test(s.textContent))
          )
            isFixedViewport = true;
        });
        const bodyStyle =
          doc.body?.getAttribute('style') ||
          (doc.defaultView
            ? doc.defaultView.getComputedStyle(doc.body).overflow
            : '');
        if (/hidden/.test(bodyStyle)) isFixedViewport = true;

        if (isFixedViewport) {
          setContentHeight(VIEWPORT_HEIGHT);
          return;
        }

        // Override height so content flows naturally
        const overrideStyle = doc.createElement('style');
        overrideStyle.textContent =
          'html, body { height: auto !important; min-height: 0 !important; overflow: visible !important; }';
        doc.head.appendChild(overrideStyle);

        // Set height immediately, then observe for changes
        setContentHeight(measureHeight(doc));

        if (resizeObserver) resizeObserver.disconnect();
        resizeObserver = new ResizeObserver(() => {
          try {
            setContentHeight(measureHeight(doc));
          } catch {
            setContentHeight(minHeight);
          }
        });
        resizeObserver.observe(doc.body);
      } catch {
        setContentHeight(minHeight);
      }
    };

    iframe.addEventListener('load', handleLoad);
    if (iframe.contentDocument?.readyState === 'complete') handleLoad();

    return () => {
      iframe.removeEventListener('load', handleLoad);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [htmlBlob, htmlSrc, minHeight, usesViewport]);

  if (!htmlSrc && !htmlBlob) {
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          color: '#666',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#f9f9f9',
          minHeight: minHeight + 'px',
        }}
      >
        No content provided
      </div>
    );
  }

  const usesSrcdoc = !htmlSrc && htmlBlob;
  const iframeSrc = htmlSrc || undefined;
  const iframeSrcdoc = usesSrcdoc ? createFullHTML(htmlBlob!) : undefined;

  const height = contentHeight ?? minHeight;

  return (
    <div
      className={`simple-interactive-renderer ${className}`}
      style={{ width: '100%', height: height + 'px', ...style }}
    >
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        srcDoc={iframeSrcdoc}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          display: 'block',
        }}
        sandbox="allow-scripts allow-same-origin allow-forms"
        title="Interactive Content"
        allow="accelerometer; camera; encrypted-media; gyroscope; microphone"
      />
    </div>
  );
};

export default SimpleInteractiveRenderer;
